// TanStack Query hooks for transform_runs.
// Mirrors useMaterializations polling pattern.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { TransformRun } from "@/types/ontology";

// ── Query keys (plan §1.3) ────────────────────────────────────────────────────

export const TRANSFORM_RUNS_KEY = ["transform-runs"] as const;

export function transformRunsKey(transformId?: string) {
  return transformId ? [...TRANSFORM_RUNS_KEY, transformId] : TRANSFORM_RUNS_KEY;
}

// ── Queries ───────────────────────────────────────────────────────────────────

/**
 * List transform runs for a given transform (or all runs if transformId is undefined).
 * Polls every 5s while any run has status === 'running'.
 */
export function useTransformRuns(transformId?: string) {
  const query = useQuery({
    queryKey: transformRunsKey(transformId),
    queryFn: async () => {
      let q = supabase
        .from("transform_runs")
        .select("*")
        .order("started_at", { ascending: false })
        .limit(50);

      if (transformId) {
        q = q.eq("transform_id", transformId);
      }

      const { data, error } = await q;

      if (error) throw new Error(error.message);

      return (data as TransformRun[]) ?? [];
    },
    // Poll every 5s while any run is still running; stop when all are terminal.
    refetchInterval: (query) => {
      const runs = query.state.data as TransformRun[] | undefined;
      if (!runs) return false;
      return runs.some((r) => r.status === "running") ? 5_000 : false;
    },
  });

  return {
    runs: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** Single run with 5s polling while status === 'running'. */
export function useTransformRun(id: string | null) {
  const query = useQuery({
    queryKey: [...TRANSFORM_RUNS_KEY, "single", id],
    enabled: id !== null,
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("transform_runs")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw new Error(error.message);
      return (data as TransformRun) ?? null;
    },
    refetchInterval: (query) => {
      const run = query.state.data as TransformRun | null;
      if (!run) return false;
      return run.status === "running" ? 5_000 : false;
    },
  });

  return {
    run: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}
